const CHAT_TIMESTAMP_GAP_MS = 30 * 60 * 1000;

const isSameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const parseChatTimestamp = (createdAt: string): Date | undefined => {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return;
  return date;
};

/**
 * A thread stamp is relevant on the first message, when the local day
 * changes, and when a burst of messages is separated by at least 30 minutes.
 */
export const shouldShowChatTimestamp = (
  createdAt: string,
  previousCreatedAt: string | undefined,
): boolean => {
  const date = parseChatTimestamp(createdAt);
  if (!date) return false;
  if (previousCreatedAt === undefined) return true;
  const previous = parseChatTimestamp(previousCreatedAt);
  if (!previous) return true;
  if (!isSameLocalDay(date, previous)) return true;
  return date.getTime() - previous.getTime() >= CHAT_TIMESTAMP_GAP_MS;
};

const formatChatClockTime = (date: Date): string =>
  date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

/** Clock time under a hovered message, e.g. `16:58`. */
export const formatChatMessageTime = (
  createdAt: string,
): string | undefined => {
  const date = parseChatTimestamp(createdAt);
  if (!date) return;
  return formatChatClockTime(date);
};

/** `Today 15:43`, `Yesterday 15:43`, or `Sep 22, 15:43`. */
export const formatChatThreadTimestamp = (
  createdAt: string,
  now: Date,
): string | undefined => {
  const date = parseChatTimestamp(createdAt);
  if (!date) return;
  const time = formatChatClockTime(date);
  if (isSameLocalDay(date, now)) return `Today ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameLocalDay(date, yesterday)) return `Yesterday ${time}`;
  const dateLabel = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
  return `${dateLabel}, ${time}`;
};
